import { ReactElement } from "react";

import { Colors } from "@blueprintjs/core";
import styled from "@emotion/styled";
import { BookOpen, Home, Settings } from "lucide-react";
import { useLocation } from "react-router";

import {
  isCoach,
  isWorkspaceAdmin,
  isWorkspaceGroupAdmin,
} from "@/components/helpers/workspace/permissions";
import { Box, Flex } from "@/components/layout/flexbox";
import { useAuth } from "@/components/lib/auth";
import { CallScoringIcon } from "@/components/pages_logged_in/call-scoring/CallScoringIcon";
import { HelpMenu } from "@/components/pages_logged_in/workspace/nav/SideNav/HelpMenu";
import { SideNavSkeleton } from "@/components/pages_logged_in/workspace/nav/SideNav/SideNavSkeleton";
import { WorkspaceSwitching } from "@/components/pages_logged_in/workspace/nav/SideNav/WorkspaceSwitching";
import { FlipIcon } from "@/components/pieces/icons/FlipIcon";
import { CustomIcon } from "@/components/pieces/icons/Icons";
import { AnalyticsSvg } from "@/components/pieces/icons/svg/analytics";
import { CertificationsSvg } from "@/components/pieces/icons/svg/certifications";
import {
  ClientsDeselected as ClientsDeselectedIcon,
  ClientsSelected as ClientsSelectedIcon,
} from "@/components/pieces/icons/svg/clients";
import { CoachingSvg } from "@/components/pieces/icons/svg/coaching";
import { PlaybookSvg } from "@/components/pieces/icons/svg/playbook";
import { PracticeSvg } from "@/components/pieces/icons/svg/practice";
import { RubricsSvg } from "@/components/pieces/icons/svg/rubrics";
import { getUser } from "@/components/session/JwtTokenManager";
import { BRAND_PURPLE, LIGHT_BRAND_PURPLE, NAV_Z_INDEX } from "@/css/constants";
import { formatDate } from "@/utils/datetime";

import { SideMenuItem } from "./SideMenuItem";

type Menu =
  | "home"
  | "coaching"
  | "referrals"
  | "client"
  | "roleplays"
  | "chat"
  | "learn"
  | "analytics"
  | "program"
  | "settings"
  | "call-scoring"
  | "workspace"
  | "rubrics"
  | "certifications";

const Divider = () => <div className="border-b border-border my-3" />;

export function SideNavContent({ pageLoading }: { pageLoading: boolean }) {
  const { currentWorkspace } = useAuth();
  const user = getUser();
  const location = useLocation();

  if (!user) {
    return <SideNavSkeleton />;
  }

  const { pathname } = location;
  let activeMenuItem: Menu = "home";

  if (pathname === "/home") {
    activeMenuItem = "home";
  } else if (pathname === "/coaching") {
    activeMenuItem = "coaching";
  } else if (pathname === "/referrals") {
    activeMenuItem = "referrals";
  } else if (pathname.startsWith("/client")) {
    activeMenuItem = "client";
  } else if (pathname.startsWith("/roleplays")) {
    activeMenuItem = "roleplays";
  } else if (pathname.startsWith("/messages")) {
    activeMenuItem = "chat";
  } else if (pathname.startsWith("/learn")) {
    activeMenuItem = "learn";
  } else if (pathname.startsWith("/rubrics")) {
    activeMenuItem = "rubrics";
  } else if (pathname.startsWith("/certifications")) {
    activeMenuItem = "certifications";
  } else if (pathname.startsWith("/analytics")) {
    activeMenuItem = "analytics";
  } else if (pathname.startsWith("/calls")) {
    activeMenuItem = "call-scoring";
  } else if (
    pathname.startsWith("/sessions") ||
    pathname.startsWith("/reviews") ||
    pathname.startsWith("/book") ||
    pathname.startsWith("/schedule") ||
    pathname.startsWith("/coaching") ||
    pathname.startsWith("/buy") ||
    pathname.startsWith("/services") ||
    pathname.startsWith("/payouts") ||
    pathname.startsWith("/availability")
  ) {
    activeMenuItem = "coaching";
  } else if (pathname.startsWith("/workspace")) {
    activeMenuItem = "settings";
  } else if (pathname.startsWith("/programs")) {
    activeMenuItem = "program";
  }

  const programItem = currentWorkspace.programsEnabled ? (
    <SideMenuItem
      icon={
        <PlaybookSvg
          size={16}
          color={activeMenuItem === "program" ? BRAND_PURPLE : "#BFBFBF"}
        />
      }
      label="Programs"
      active={activeMenuItem === "program"}
      link="/programs"
    />
  ) : null;

  const callScoringItem = currentWorkspace.callScoringEnabled ? (
    <SideMenuItem
      icon={
        <CallScoringIcon
          selected={activeMenuItem === "call-scoring"}
          className="w-4 h-4"
        />
      }
      label="Call Scoring"
      active={activeMenuItem === "call-scoring"}
      link="/calls/getting-started"
    />
  ) : null;

  const roleplaysItem = currentWorkspace.roleplaysEnabled ? (
    <SideMenuItem
      icon={
        <PracticeSvg
          size={16}
          color={activeMenuItem === "roleplays" ? BRAND_PURPLE : "#BFBFBF"}
        />
      }
      label="Roleplays"
      active={activeMenuItem === "roleplays"}
      link="/roleplays"
    />
  ) : null;

  const workspaceItems = (
    <>
      {isWorkspaceAdmin() ? <Divider /> : null}
      {isWorkspaceGroupAdmin() && !isWorkspaceAdmin() && (
        <WorkspaceNavItem
          label="Groups"
          active={activeMenuItem === "settings"}
        />
      )}
      {isWorkspaceAdmin() && currentWorkspace.roleplaysEnabled && (
        <RubricsNavItem active={activeMenuItem === "rubrics"} />
      )}
      {isWorkspaceAdmin() && currentWorkspace.certificationsEnabled && (
        <CertificationsNavItem active={activeMenuItem === "certifications"} />
      )}
      {isWorkspaceAdmin() &&
        user?.workspaces?.some((obj) => obj?.analyticsEnabled) && (
          <AnalyticsNavItem active={activeMenuItem === "analytics"} />
        )}
      {isWorkspaceAdmin() && (
        <WorkspaceNavItem active={activeMenuItem === "settings"} />
      )}
    </>
  );
  const homeItem = !currentWorkspace.isPersonal ? (
    <HomeNavItem active={activeMenuItem === "home"} />
  ) : null;

  const coachItems = (
    <>
      {homeItem}
      <CoachingNavItem
        active={activeMenuItem === "coaching"}
        label={"Coaching"}
      />
      <SideMenuItem
        icon={<ClientIcon active={activeMenuItem === "client"} />}
        label="Clients"
        active={activeMenuItem === "client"}
        link="/clients"
      />
      <Divider />
      {programItem}
      {callScoringItem}
      {roleplaysItem}
      <LearnNavItem active={activeMenuItem === "learn"} />
    </>
  );

  const clientItems = (
    <>
      {homeItem}
      <CoachingNavItem
        active={activeMenuItem === "coaching"}
        label={"Coaching"}
      />
      {programItem}
      {callScoringItem}
      {roleplaysItem}
      <LearnNavItem active={activeMenuItem === "learn"} />
    </>
  );

  return (
    <Flex flexDirection="column" justifyContent="space-between" height="100%">
      <Box>
        <WorkspaceSwitching pageLoading={pageLoading} />
        <Box px={2} py={3}>
          {isCoach() ? coachItems : clientItems}
          {!currentWorkspace.isPersonal &&
          (isWorkspaceAdmin() || isWorkspaceGroupAdmin())
            ? workspaceItems
            : null}
        </Box>
      </Box>
      <Box px={2} pb={2}>
        {PR_PREVIEW_DEPLOYED_AT && (
          <div className="mb-4 p-2 text-center border bg-gray-200 rounded-md">
            <div className="mb-1 text-xs text-muted-foreground">
              Preview Deployed
            </div>
            <div className="font-mono text-[0.6rem] leading-tight text-muted-foreground break-words">
              {formatDate(
                new Date(PR_PREVIEW_DEPLOYED_AT),
                "MMM d, yyyy h:mm:ss a z",
              )}
            </div>
          </div>
        )}
        {currentWorkspace.isPersonal ? (
          <SideMenuItem
            icon={
              <CustomIcon
                icon="gift"
                iconSize={16}
                color={
                  activeMenuItem === "referrals" ? BRAND_PURPLE : "#bfbfbf"
                }
              />
            }
            label="Referrals"
            active={activeMenuItem === "referrals"}
            link="/referrals"
          />
        ) : (
          <HelpMenu />
        )}
      </Box>
    </Flex>
  );
}

function HomeNavItem({ active }: { active: boolean }) {
  return (
    <SideMenuItem icon={Home} label="Home" active={active} link={"/home"} />
  );
}

function CoachingNavItem({
  active,
  icon = <CoachingSvg size={16} color={active ? BRAND_PURPLE : "#BFBFBF"} />,
  label = "Coaching",
}: {
  active: boolean;
  icon?: ReactElement;
  label?: string;
}) {
  return (
    <SideMenuItem
      icon={icon}
      label={label}
      active={active}
      link={"/coaching"}
    />
  );
}

function RubricsNavItem({ active }: { active: boolean }) {
  return (
    <SideMenuItem
      icon={
        <RubricsSvg
          size={16}
          mainColor={active ? BRAND_PURPLE : "#BFBFBF"}
          lighterColor={active ? LIGHT_BRAND_PURPLE : "#E1E1E1"}
        />
      }
      label="Rubrics"
      active={active}
      link={"/rubrics"}
    />
  );
}

function CertificationsNavItem({ active }: { active: boolean }) {
  return (
    <SideMenuItem
      icon={
        <CertificationsSvg
          size={16}
          mainColor={active ? BRAND_PURPLE : "#BFBFBF"}
          lighterColor={active ? LIGHT_BRAND_PURPLE : "#E1E1E1"}
        />
      }
      label="Certifications"
      active={active}
      link={"/certifications"}
    />
  );
}

function AnalyticsNavItem({ active }: { active: boolean }) {
  return (
    <SideMenuItem
      icon={
        <AnalyticsSvg
          size={16}
          mainColor={active ? BRAND_PURPLE : "#BFBFBF"}
          lighterColor={active ? LIGHT_BRAND_PURPLE : "#E1E1E1"}
        />
      }
      label="Analytics"
      active={active}
      link={"/analytics"}
    />
  );
}

interface WorkspaceNavItemProps {
  active: boolean;
  label?: string;
}

function WorkspaceNavItem({
  active,
  label = "Settings",
}: WorkspaceNavItemProps) {
  return (
    <SideMenuItem
      icon={Settings}
      label={label}
      active={active}
      link={isWorkspaceAdmin() ? "/workspace/users" : "/workspace/groups"}
    />
  );
}

function LearnNavItem({ active }: { active: boolean }) {
  return (
    <SideMenuItem
      icon={BookOpen}
      label="Guides"
      active={active}
      link="/learn"
    />
  );
}

function ClientIcon({ active }: { active: boolean }) {
  return (
    <FlipIcon
      activeIcon={<ClientsSelectedIcon size={16} />}
      inactiveIcon={<ClientsDeselectedIcon size={16} />}
      isActive={active}
    />
  );
}

const SideNavContainer = styled(Box)`
  position: fixed;
  border-right: 1px solid ${Colors.LIGHT_GRAY3};
  top: 0;
  bottom: 0;
  left: 0;
  width: ${(props) => props.width}px;
  z-index: ${NAV_Z_INDEX};
  background-color: ${Colors.WHITE};
  overflow-y: auto;
`;

export function SideNav({
  width,
  pageLoading,
}: {
  width: number;
  pageLoading: boolean;
}) {
  return (
    <SideNavContainer width={width}>
      <SideNavContent pageLoading={pageLoading} />
    </SideNavContainer>
  );
}
